import {useContext, useState, useRef, MutableRefObject} from 'react';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useParams, useRouteMatch} from 'react-router-dom';
import {AuthContext} from '../../../../../../contexts/auth/context';
import {AuthContextType} from '../../../../../../contexts/auth/types';
import {ApplicationsRepository} from '../../../../../../repositories/applications';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {CategorizedApplication} from '../../../../../../backend/responses/categorized-application';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {Box, Stack} from '@mui/material';
import {ApplicationsStateModal} from '../../../../../../components/user-applications/modals/applications-state-modal';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {ScreenPaths} from '../../../../../../enums';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Category = 'category',
  State = 'state',
}

interface ApplicationsProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
}

export default function ApplicationsProfessionalCaregiverOfficeScreen({
  canWrite,
}: ApplicationsProfessionalCaregiverOfficeScreenProps) {
  // Get params
  let {entityId}: {entityId?: string} = useParams();

  // Get context
  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Hold state
  const [updateApplicationDialogOpened, setUpdateApplicationDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {applicationId: string; applicationName: string; applicationActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch applications
  const handleGetApplications = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!entityId) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load applications
    const result: {totalCount: number; data: Array<CategorizedApplication>} =
      await ApplicationsRepository.instance.getInEntityAsAffiliatedForEntity(
        entityId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<CategorizedApplication> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.application.id || '',
        item: obj,
        enabled: obj.application.isActive,
        avatarUrl: obj.application.picture,
        toLink:
          url.charAt(url.length - 1) === '/'
            ? `${url}${obj.application.id}`
            : `${url}/${obj.application.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.application.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.application.isActive
              ? t(translations.applications.professional_caregiver.root.state_value_true)
              : t(translations.applications.professional_caregiver.root.state_value_false),
          },
          {
            columnId: ColumnIdentifier.Category,
            // The name is localized
            value: obj.category.name || '',
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<CategorizedApplication>({
    fetchData: handleGetApplications,
  });

  const continueUpdateApplicationState = async (
    entityId: string,
    applicationId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await ApplicationsRepository.instance.updateActiveStateForAllUsersAsAffiliatedForEntity(
        entityId,
        applicationId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.applications.professional_caregiver.root.state_value_true)
              : t(translations.applications.professional_caregiver.root.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.applications.professional_caregiver.root.errors
                .enable_application_error_message
            )
          : t(
              translations.applications.professional_caregiver.root.errors
                .disable_application_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the application with id ${applicationId} to ${value}.`,
        error
      );
    }
  };

  // Enable/disable application
  const handleUpdateApplicationState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const applicationId = dataToUpdateRef.current.applicationId;
      const value = dataToUpdateRef.current.applicationActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.applications.professional_caregiver.root.state_value_true)
              : t(translations.applications.professional_caregiver.root.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it can take some time to update
      continueUpdateApplicationState(entityId || '', applicationId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.applications.professional_caregiver.root.errors
            .update_state_application_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the application. Information was missing to perform the request.'
      );
    }
  };

  const handleAskUpdateApplicationState = (applicationId: string, value: boolean) => {
    // Get application name
    const applicationName: string | undefined = data
      .find((obj) => obj.id === applicationId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set application info for later
    dataToUpdateRef.current = {
      applicationId: applicationId,
      applicationActiveFlag: value,
      applicationName: applicationName || '',
    };

    // Open modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdateApplicationState = () => {
    // Close modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateApplicationState = () => {
    // Close modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateApplicationState();
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.applications),
            link: `${url}`,
          },
        ]}
        isLoading={isLoading}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.user_applications.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={null}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(
              translations.applications.professional_caregiver.root.no_applications
            )}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.applications.professional_caregiver.root.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Category,
                title: t(translations.applications.professional_caregiver.root.category_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.applications.professional_caregiver.root.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.applications.professional_caregiver.root.state_value_true
                    ),
                    handleClick: handleAskUpdateApplicationState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_applications.disabled_state_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      <ApplicationsStateModal
        open={
          updateApplicationDialogOpened.disableDialogOpened ||
          updateApplicationDialogOpened.enableDialogOpened
        }
        state={updateApplicationDialogOpened.disableDialogOpened ? 'disable' : 'enable'}
        onClose={handleDeclineUpdateApplicationState}
        onConfirm={handleConfirmUpdateApplicationState}
        applicationName={dataToUpdateRef.current?.applicationName || ''}
        description={t(
          translations.applications.professional_caregiver.root[
            `${updateApplicationDialogOpened.disableDialogOpened ? 'disable' : 'enable'}_application_dialog_message`
          ],
          {
            name: authContext.state.session?.selectedAffiliation?.entity.name || '',
          }
        )}
      />
    </Box>
  );
}
